import {
  createDirectus,
  rest,
  realtime,
  staticToken,
  readMe,
  authentication,
  refresh,
  passwordRequest,
  readItems,
  readItem,
  updateItem,
  createItem,
  deleteItem,
  readUsers,
  readRoles,
  updateUser,
  createUser,
  deleteUser,
  updateRole,
  createRole,
  deleteRole,
} from '@directus/sdk';
import { boot } from 'quasar/wrappers';
import { Notify } from 'quasar';

const REFRESH_LOCK_KEY = 'crm_refresh_lock';
const LOCK_TIMEOUT = 5000; // 5 seconds
const REFRESH_INTERVAL = 12 * 60 * 60 * 1000; // 12 hours

/** One persistent WebSocket for all $crm.subscribe() calls. */
let sharedRealtimeClient = null;
let sharedRealtimeToken = null;
let sharedRealtimeConnecting = null; // in-flight connect() promise (shared by concurrent callers)

async function ensureSharedRealtimeConnected() {
  const currentToken = localStorage.getItem('crm_auth_token');
  if (!currentToken) {
    return null;
  }
  if (sharedRealtimeClient && sharedRealtimeToken !== currentToken) {
    try { sharedRealtimeClient.disconnect(); } catch (e) { /* noop */ }
    sharedRealtimeClient = null;
    sharedRealtimeToken = null;
    sharedRealtimeConnecting = null;
  }
  if (!sharedRealtimeClient) {
    sharedRealtimeClient = createDirectus(process.env.CRM_URL)
      .with(staticToken(currentToken))
      .with(realtime());
    sharedRealtimeToken = currentToken;

    sharedRealtimeConnecting = Promise.resolve(sharedRealtimeClient.connect())
      .catch((e) => {
        // connect failed → reset so a later call retries from scratch
        sharedRealtimeClient = null;
        sharedRealtimeToken = null;
        throw e;
      })
      .finally(() => { sharedRealtimeConnecting = null; });
  }
  if (sharedRealtimeConnecting) {
    try { await sharedRealtimeConnecting; } catch (e) { return null; }
  }
  return sharedRealtimeClient;
}


export default boot(async ({ app, router }) => {
  const authToken = localStorage.getItem('crm_auth_token');

  let directus = createDirectus(process.env.CRM_URL)
    .with(rest())
    .with(authentication('json'));

  if (authToken) {
    directus = directus.with(staticToken(authToken));
  }

  const CRM_CORE_COLLECTIONS = ['users', 'directus_users', 'directus_roles'];
  
  const queryCRM = async (collection, query = {}, authToken = null) => {
    const token = authToken || localStorage.getItem('crm_auth_token');
    if (!token) {
      return null;
    }

    const crmUrl = process.env.CRM_URL;
    
    const queryParams = new URLSearchParams();
    
    if (query.fields) {
      queryParams.append('fields', Array.isArray(query.fields) ? query.fields.join(',') : query.fields);
    }
    if (query.limit !== undefined) {
      queryParams.append('limit', query.limit);
    }
    if (query.filter) {
      queryParams.append('filter', JSON.stringify(query.filter));
    }
    if (query.sort) {
      queryParams.append('sort', Array.isArray(query.sort) ? query.sort.join(',') : query.sort);
    }
    if (query.page) {
      queryParams.append('page', query.page);
    }
    if (query.offset) {
      queryParams.append('offset', query.offset);
    }
    if (query.meta) {
      queryParams.append('meta', Array.isArray(query.meta) ? query.meta.join(',') : query.meta);
    }
    
    const queryString = queryParams.toString();
    const url = `${crmUrl}${collection}${queryString ? `?${queryString}` : ''}`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error querying CRM:', error);
      return null;
    }
  };

  const getFreshInstance = () => {
    const currentToken = localStorage.getItem('crm_auth_token');
    
    let instance = createDirectus(process.env.CRM_URL).with(rest());
    
    if (currentToken) {
      instance = instance.with(staticToken(currentToken));
    } else {
      instance = instance.with(authentication('json'));
    }
    
    return instance;
  };

  const createCrmWrapper = (directusInstance) => {
    return {
      url: process.env.CRM_URL,
      request: async (...args) => {
        const freshInstance = getFreshInstance();
        return await freshInstance.request(...args);
      },
      
      login: async (credentials) => {
        const { email, password } = credentials;
        const loginClient = createDirectus(process.env.CRM_URL)
          .with(rest())
          .with(authentication('json'));
        return await loginClient.login({ email, password });
      },
      
      passwordRequest: async (email, options = {}) => {
        const freshInstance = getFreshInstance();
        return await freshInstance.request(passwordRequest(email, options));
      },
      
      readMe: async (options) => {
        const freshInstance = getFreshInstance();
        if (options) {
          return await freshInstance.request(readMe(options));
        }
        return await freshInstance.request(readMe());
      },
      
      items: (collection) => {
        const isCoreCollection = CRM_CORE_COLLECTIONS.includes(collection);
        
        return {
          readByQuery: async (query = {}) => {
            const normalizedQuery = { ...query };
            if (normalizedQuery.meta && Array.isArray(normalizedQuery.meta)) {
              normalizedQuery.meta = normalizedQuery.meta.join(',');
            }
            
            let result;
            if (isCoreCollection) {
              const currentToken = localStorage.getItem('crm_auth_token');
              if (!currentToken) {
                return { data: [] };
              }
              const freshInstance = getFreshInstance();
              if (collection === 'users' || collection === 'directus_users') {
                result = await freshInstance.request(readUsers(normalizedQuery));
              } else if (collection === 'directus_roles') {
                result = await freshInstance.request(readRoles(normalizedQuery));
              } else {
                result = await freshInstance.request(readItems(collection, normalizedQuery));
              }
            } else {
              const currentToken = localStorage.getItem('crm_auth_token');
              if (!currentToken) {
                return { data: [] };
              }
              const freshInstance = getFreshInstance();
              result = await freshInstance.request(readItems(collection, normalizedQuery));
            }
            
            if (result && !result.data && Array.isArray(result)) {
              return { data: result };
            } else if (result && result.data) {
              return result;
            } else if (result) {
              return { data: result };
            }
            return { data: [] };
          },
          readOne: async (id, options = {}) => {
            if (collection === 'users' || collection === 'directus_users') {
              const currentToken = localStorage.getItem('crm_auth_token');
              if (!currentToken) {
                return null;
              }
              const freshInstance = getFreshInstance();
              const query = {
                filter: {
                  id: {
                    _eq: id
                  }
                },
                ...options
              };
              const result = await freshInstance.request(readUsers(query));
              return result?.data?.[0] || result?.[0] || null;
            }
            if (collection === 'directus_roles') {
              const currentToken = localStorage.getItem('crm_auth_token');
              if (!currentToken) {
                return null;
              }
              const freshInstance = getFreshInstance();
              const query = {
                filter: {
                  id: {
                    _eq: id
                  }
                },
                ...options
              };
              const result = await freshInstance.request(readRoles(query));
              return result?.data?.[0] || result?.[0] || null;
            }
            const currentToken = localStorage.getItem('crm_auth_token');
            if (!currentToken) {
              return null;
            }
            const freshInstance = getFreshInstance();
            const response = await freshInstance.request(readItem(collection, id, options));
            return response?.data || response;
          },
          updateOne: async (id, body) => {
            const currentToken = localStorage.getItem('crm_auth_token');
            if (!currentToken) {
              throw new Error('No authentication token available for update operation');
            }
            const freshInstance = getFreshInstance();
            if (isCoreCollection) {
              if (collection === 'users' || collection === 'directus_users') {
                return await freshInstance.request(updateUser(id, body));
              }
              if (collection === 'directus_roles') {
                return await freshInstance.request(updateRole(id, body));
              }
            }
            return await freshInstance.request(updateItem(collection, id, body));
          },
          createOne: async (body) => {
            const currentToken = localStorage.getItem('crm_auth_token');
            if (!currentToken) {
              throw new Error('No authentication token available for create operation');
            }
            const freshInstance = getFreshInstance();
            if (isCoreCollection) {
              if (collection === 'users' || collection === 'directus_users') {
                return await freshInstance.request(createUser(body));
              }
              if (collection === 'directus_roles') {
                return await freshInstance.request(createRole(body));
              }
            }
            return await freshInstance.request(createItem(collection, body));
          },
          deleteOne: async (id) => {
            const currentToken = localStorage.getItem('crm_auth_token');
            if (!currentToken) {
              throw new Error('No authentication token available for delete operation');
            }
            const freshInstance = getFreshInstance();
            if (isCoreCollection) {
              if (collection === 'users' || collection === 'directus_users') {
                return await freshInstance.request(deleteUser(id));
              }
              if (collection === 'directus_roles') {
                return await freshInstance.request(deleteRole(id));
              }
            }
            return await freshInstance.request(deleteItem(collection, id));
          },
          getCount: async (filter = {}) => {
            const currentToken = localStorage.getItem('crm_auth_token');
            if (!currentToken) {
              return 0;
            }
            try {
              const filterParam = Object.keys(filter).length > 0 
                ? `&filter=${encodeURIComponent(JSON.stringify(filter))}` 
                : '';
              const path = `items/${collection}?aggregate[count]=*${filterParam}`;
              const crmUrl = process.env.CRM_URL;
              const url = `${crmUrl}${path}`;
              
              const response = await fetch(url, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${currentToken}`,
                  'Content-Type': 'application/json'
                }
              });
              
              const data = await response.json();
              
              if (Array.isArray(data?.data) && data.data.length > 0) {
                const count = data.data[0].count;
                return typeof count === 'string' ? parseInt(count, 10) : count || 0;
              }
              return 0;
            } catch (error) {
              console.error('Error getting count:', error);
              return 0;
            }
          }
        };
      },

      sendWsMessage: async (message) => {
        const client = await ensureSharedRealtimeConnected();
        if (!client) return false;
        try {
          client.sendMessage(message);
          return true;
        } catch (err) {
          // Socket dropped after connecting -> reopen this client and retry once.
          try {
            await client.connect();
            client.sendMessage(message);
            return true;
          } catch (err2) {
            console.error('[crm] sendWsMessage failed after reconnect:', err2);
            return false;
          }
        }
      },
      // Listen for raw incoming WebSocket messages on the shared connection.
      // Returns a Promise<unsubscribe>. The caller MUST call the returned
      // function to detach the listener (typically in beforeUnmount).
      onWsMessage: async (handler) => {
        const client = await ensureSharedRealtimeConnected();
        if (!client) return () => {};
        try {
          const off = client.onWebSocket('message', handler);
          return typeof off === 'function' ? off : (() => {});
        } catch (err) {
          console.error('[crm] onWsMessage failed:', err);
          return () => {};
        }
      },
      subscribe: async (collection, callback, options = {}) => {
        const currentToken = localStorage.getItem('crm_auth_token');
        if (!currentToken) {
          return null;
        }
        try {
          const subClient = await ensureSharedRealtimeConnected();
          if (!subClient) {
            return null;
          }
          const { subscription, unsubscribe: sdkUnsubscribe } = await subClient.subscribe(collection, {
            query: { fields: ['*'] },
            ...options
          });
          (async () => {
            try {
              for await (const message of subscription) {
                callback(message);
              }
            } catch (err) {
              console.error(`Subscription iterator error for ${collection}:`, err);
            }
          })();

          return {
            unsubscribe: () => {
              try {
                sdkUnsubscribe();
              } catch (e) {
                console.error('Error unsubscribing from realtime:', e);
              }
            }
          };
        } catch (error) {
          console.error(`Error subscribing to ${collection}:`, error);
          return null;
        }
      },
      query: queryCRM
    };
  };

  const updateCrmWrapper = () => {
    const crmWrapper = createCrmWrapper(directus);
    app.config.globalProperties.$crm = crmWrapper;
    if (typeof window !== 'undefined') {
      window.$crm = crmWrapper;
    }
  };

  // ---------- Refresh lock helpers ----------
  const isRefreshLocked = () => {
    const lock = localStorage.getItem(REFRESH_LOCK_KEY);
    if (!lock) return false;
    return parseInt(lock, 10) + LOCK_TIMEOUT > Date.now();
  };

  const setRefreshLock = () => {
    localStorage.setItem(REFRESH_LOCK_KEY, Date.now().toString());
  };

  const releaseRefreshLock = () => {
    localStorage.removeItem(REFRESH_LOCK_KEY);
  };

  // ---------- Token refresh ----------
  const refreshToken = async () => {
    console.log('refreshToken');
    if (isRefreshLocked()) {
      setTimeout(refreshToken, 1000);
      return;
    }

    setRefreshLock();

    try {
      const refreshTokenValue = localStorage.getItem('crm_refresh_token');

      if (!refreshTokenValue) {
        throw new Error('No refresh token available');
      }

      // Use SDK's refresh method
      const result = await directus.request(refresh('json', refreshTokenValue));

      if (!result?.access_token) {
        throw new Error('Invalid refresh response');
      }

      const { access_token, refresh_token } = result;

      localStorage.setItem('crm_auth_token', access_token);
      localStorage.setItem('crm_refresh_token', refresh_token);

      // 🔁 Recreate client with new token and authentication
      directus = createDirectus(process.env.CRM_URL)
        .with(rest())
        .with(authentication('json'))
        .with(staticToken(access_token));

      updateCrmWrapper();

    } catch (error) {
      console.error('CRM token refresh failed:', error);

      Notify.create({
        type: 'negative',
        caption: 'Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an.',
      });

      localStorage.removeItem('crm_auth_token');
      localStorage.removeItem('crm_refresh_token');

      await router.push({
        path: '/auth/login',
        query: { removeToken: true },
      });
    } finally {
      releaseRefreshLock();
    }
  };

  // ---------- Refresh interval ----------
  const setupRefreshInterval = () => {
    setInterval(refreshToken, REFRESH_INTERVAL);
  };

  // ---------- Optional: validate token on startup ----------
  if (authToken) {
    try {
      await directus.request(readMe());
    } catch {
      await refreshToken();
    }
  }

  setupRefreshInterval();

  // ---------- Global exposure ----------
  updateCrmWrapper();
});
